import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, RefreshCw, Eye, EyeOff, CheckCircle2, Pencil, Ban } from 'lucide-react';
import api from '../lib/api';

const SEV_STYLES = {
  high: 'bg-heritage text-base',
  medium: 'bg-mustard text-charcoal',
  low: 'bg-charcoal/10 text-charcoal',
};

// kind -> where the admin can edit that content
const EDIT_PATHS = {
  digest: '/admin/digest',
  newsletter: '/admin/newsletter',
  event: '/admin/events',
  attraction: '/admin/attractions',
  member: '/admin/members',
  package: '/admin/packages',
};
const HIDEABLE = new Set(['attraction', 'event', 'newsletter', 'digest']);

export default function AdminSentinel() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [run, setRun] = useState(null);
  const [scanning, setScanning] = useState(false);

  function load() {
    api.get(`/admin/sentinel/flags?status=${tab}`).then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); }).catch(() => {});
    api.get('/admin/sentinel/runs/latest').then((r) => setRun(r.data.run)).catch(() => {});
  }
  useEffect(load, [tab]);

  useEffect(() => {
    if (run?.status !== 'running') return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [run?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function scanNow() {
    setScanning(true);
    try { await api.post('/admin/sentinel/scan-now'); } catch (e) { alert(e.response?.data?.detail || 'Could not start scan'); }
    setScanning(false);
    load();
  }
  async function act(id, action) {
    try { await api.post(`/admin/sentinel/flags/${id}/${action}`); }
    catch (e) { alert(e.response?.data?.detail || 'Action failed'); }
    load();
  }

  async function bulkApprove() {
    const liveCount = items.filter((f) => !f.held).length;
    if (!window.confirm(`Approve all ${liveCount} still-live flagged items in one go? Held (hidden) items are not touched.`)) return;
    try {
      const r = await api.post('/admin/sentinel/flags/bulk-approve');
      alert(`${r.data.approved} flags approved.`);
    } catch (e) { alert(e.response?.data?.detail || 'Bulk approve failed'); }
    load();
  }

  return (
    <div data-testid="sentinel-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="label text-heritage mb-2 inline-flex items-center gap-2"><ShieldCheck size={14}/> Reassurance filter</div>
          <h1 className="font-display text-4xl">Sensitivity review queue.</h1>
          <p className="text-muted mt-2 max-w-2xl text-sm">Every piece of public copy is AI-scanned nightly at 9pm UK time, and instantly when new copy is drafted. High/medium-risk items are hidden until you review them; low-risk items stay live but wait here for a tone-check. Your review decisions are remembered — approved copy is never re-flagged unless its wording changes.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {tab === 'pending' && items.some((f) => !f.held) && (
            <button onClick={bulkApprove} data-testid="sentinel-bulk-approve"
              className="inline-flex items-center gap-2 bg-forest text-base px-5 py-3 rounded-full font-semibold hover:bg-forest-deep">
              <CheckCircle2 size={15}/> Approve all still-live ({items.filter((f) => !f.held).length})
            </button>
          )}
          <button onClick={scanNow} disabled={scanning || run?.status === 'running'} data-testid="sentinel-scan-now"
            className="inline-flex items-center gap-2 bg-charcoal text-base px-5 py-3 rounded-full font-semibold hover:bg-charcoal/90 disabled:opacity-60">
            <RefreshCw size={15} className={run?.status === 'running' ? 'animate-spin' : ''}/>
            {run?.status === 'running' ? 'Scanning…' : 'Run full scan now'}
          </button>
        </div>
      </div>

      {run && (
        <div className="bg-white border border-charcoal/10 rounded-2xl p-4 mb-6 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="sentinel-last-run">
          <span className="font-semibold">{run.status === 'running' ? 'Scan in progress' : 'Last scan'}</span>
          <span>{new Date(run.started_at).toLocaleString('en-GB')}</span>
          <span>{run.scanned} scanned</span>
          <span>{run.skipped} unchanged (skipped)</span>
          <span className={run.flagged > 0 ? 'text-heritage font-semibold' : ''}>{run.flagged} flagged</span>
          <span className={run.held > 0 ? 'text-heritage font-semibold' : ''}>{run.held} held</span>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        {[['pending', `Needs review (${counts.pending ?? 0})`], ['restored', `Approved (${counts.restored ?? 0})`], ['kept_hidden', `Kept hidden (${counts.kept_hidden ?? 0})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sentinel-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? 'bg-charcoal text-base' : 'bg-white border border-charcoal/15'}`}>{label}</button>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-16 text-muted bg-white border border-charcoal/10 rounded-2xl" data-testid="sentinel-empty">
          {tab === 'pending' ? 'Nothing waiting for review — all clear.' : 'Nothing here yet.'}
        </div>
      )}

      <div className="space-y-4">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`sentinel-flag-${f.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${SEV_STYLES[f.severity] || 'bg-charcoal/10'}`}>{f.severity}</span>
              <span className="text-xs bg-admin border border-charcoal/10 px-2.5 py-1 rounded-full">{f.kind}</span>
              {f.held ? <span className="text-xs bg-heritage/10 text-heritage font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><EyeOff size={11}/> Hidden from site</span>
                : <span className="text-xs bg-forest/10 text-forest font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><Eye size={11}/> Still live</span>}
              {(f.categories || []).map((c) => <span key={c} className="text-xs text-charcoal/60">#{c}</span>)}
            </div>
            <div className="font-display text-xl mb-1">{f.name}</div>
            <div className="text-sm text-heritage mb-2">{f.reason}</div>
            <div className="text-xs text-muted bg-admin rounded-xl p-3 mb-3 line-clamp-3">{f.excerpt}</div>
            {tab === 'pending' ? (
              <div className="flex flex-wrap gap-3">
                <button onClick={() => act(f.id, 'restore')} data-testid={`sentinel-restore-${f.id}`}
                  className="bg-forest text-base px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-forest-deep">
                  <CheckCircle2 size={14}/> {f.held ? 'Approve & restore' : 'Approve — copy is fine'}
                </button>
                {!f.held && HIDEABLE.has(f.kind) && (
                  <button onClick={() => act(f.id, 'take-down')} data-testid={`sentinel-takedown-${f.id}`}
                    className="bg-heritage text-base px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-heritage/90">
                    <Ban size={14}/> Take down now
                  </button>
                )}
                <button onClick={() => act(f.id, 'keep-hidden')} data-testid={`sentinel-keep-hidden-${f.id}`}
                  className="bg-white border border-heritage text-heritage px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:bg-heritage/5">
                  <EyeOff size={14}/> {f.held ? 'Keep hidden' : 'Acknowledge (leave as-is)'}
                </button>
                {EDIT_PATHS[f.kind] && (
                  <Link to={EDIT_PATHS[f.kind]} data-testid={`sentinel-edit-${f.id}`}
                    className="bg-white border border-charcoal/15 px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:border-charcoal">
                    <Pencil size={14}/> Edit the content
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-xs text-muted">Reviewed {f.reviewed_at ? new Date(f.reviewed_at).toLocaleString('en-GB') : ''} {f.reviewed_by ? `by ${f.reviewed_by}` : ''}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
